import { z } from 'zod';
import { IdpSchema, OrgRoleSchema } from './enums.js';
import { OkSchema } from './common.js';
import { UserStatusSchema } from './org.js';

/** Platform-admin (system admin) console schemas: overview, org management. */

export const PlatformTimePointSchema = z.object({
  /** ISO date (week starting) label. */
  label: z.string(),
  value: z.number(),
});

export const PlatformOverviewSchema = z.object({
  totals: z.object({
    orgs: z.number(),
    activeOrgs: z.number(),
    paidOrgs: z.number(),
    users: z.number(),
    assessments: z.number(),
    responses: z.number(),
    aiJobs: z.number(),
    exports: z.number(),
    auditEvents: z.number(),
  }),
  revenue: z.object({
    /** Estimated monthly recurring revenue in pence (paid orgs × plan price). */
    mrrPence: z.number(),
    arrPence: z.number(),
    monthlyPricePence: z.number(),
    paidOrgs: z.number(),
    currency: z.string(),
  }),
  usage: z.object({
    /** Total stored bytes across every org (evidence + imported documents). */
    storageBytes: z.number(),
    evidenceBytes: z.number(),
    importBytes: z.number(),
    /** OpenAI-backed calls: AI drafting jobs + AI document-import processing + AI internal-audit clause examinations. */
    openaiCallsTotal: z.number(),
    openaiCalls30d: z.number(),
    /** ai_jobs grouped by kind, plus one `internal_audit` entry for AI auditor calls. */
    aiJobsByKind: z.array(z.object({ kind: z.string(), count: z.number() })),
  }),
  charts: z.object({
    /** New organisations per week, last 12 weeks. */
    signupsPerWeek: z.array(PlatformTimePointSchema),
    /** AI calls per week, last 12 weeks. */
    aiCallsPerWeek: z.array(PlatformTimePointSchema),
  }),
  generatedAt: z.string(),
});
export type PlatformOverview = z.infer<typeof PlatformOverviewSchema>;

/** A member of an org, as seen in the platform "Manage Orgs" detail view. */
export const AdminOrgMemberSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  role: OrgRoleSchema,
  isSystemAdmin: z.boolean(),
  idp: IdpSchema,
  status: UserStatusSchema,
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminOrgMember = z.infer<typeof AdminOrgMemberSchema>;

export const AdminOrgDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  domain: z.string().nullable(),
  plan: z.enum(['free', 'paid']),
  status: z.enum(['active', 'suspended']),
  memberCount: z.number(),
  assessmentCount: z.number(),
  createdAt: z.string(),
  members: z.array(AdminOrgMemberSchema),
});
export type AdminOrgDetail = z.infer<typeof AdminOrgDetailSchema>;

export const UpdateAdminOrgInputSchema = z.object({
  plan: z.enum(['free', 'paid']).optional(),
  status: z.enum(['active', 'suspended']).optional(),
});
export type UpdateAdminOrgInput = z.infer<typeof UpdateAdminOrgInputSchema>;

/** Result of starting impersonation. */
export const ImpersonateResultSchema = z.object({
  ok: z.literal(true),
  user: z.object({ id: z.string(), name: z.string(), email: z.string() }),
});
export type ImpersonateResult = z.infer<typeof ImpersonateResultSchema>;

/** Result of disabling/enabling a user account (system admin). */
export const UserStatusResultSchema = OkSchema.extend({ status: UserStatusSchema });
export type UserStatusResult = z.infer<typeof UserStatusResultSchema>;
